import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import {
    AdjustmentNotAvailableModal,
    AdjustmentPanel,
    FailedPayment,
    Loading,
    PaymentForm,
    PaymentFormValues,
    ReceiptTotalTitle,
    ReservationDetails,
} from '../../components';
import { useTripStoreUpdater } from '../../hooks';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { StoreState } from '../../types/store.type';
import { TripService } from '../../services/trip/tripService';
import { AppRoutes } from '../../routes';
import { AdjustmentPolicies, AdjustmentPreviewRequest, Guests, TripAdjustmentResponse } from 'guest-data-types-ts-lib';
import ddLogger from '../../logging/ddLogger';
import { PaymentUtils, ReservationUtils } from '../../utils';
import { useIntl } from 'react-intl';
import { PaymentConfirmationAdjustmentProps } from '..';
import { AdjustmentErrors } from '../../types/errors.type';
import segmentService, { Step } from '../../services/segment/segmentService';
import _ from 'lodash';
import { Currency } from '../../types/preference.type';
import { ExtendStayPreviewRequest } from 'guest-data-types-ts-lib/types/adjustments';
import { OccupancyCounts } from '../../types/occupancy';
import { OptimizelyContext } from '@optimizely/react-sdk';
import { DiscountDetail } from '../../types/receipt.type';
import {
    getErrorAddNighAdjustment,
    getLateCheckoutPreview,
    getPoolHeatPreview,
    getPublicAddNightPreview,
    getTripAddNightPreview,
    unitSupportsLateCheckout,
} from './AdjustmentHelper';
import moment from 'moment';
import { isFeatureAvailableForChannel, useFeatureConfig } from '../../services/optimizely/optimizelyHelper';
import { AdjustmentConfig, Splits } from '../../types/split.type';

export interface AdjustmentsProps {
    preview: TripAdjustmentResponse | null;
    occupancyCounts?: OccupancyCounts | null;
    initialOccupancy?: OccupancyCounts | null;
}

export const Adjustments: React.FC = () => {
    useTripStoreUpdater(false);

    const history = useHistory();
    const params = useParams<{ adjustment?: string }>();
    const intl = useIntl();
    const location = useLocation<AdjustmentsProps>();
    const { trip, user } = useSelector((store: StoreState) => store);
    const [preview, setPreview] = useState<TripAdjustmentResponse | null>(null);
    const [discountViewDetails, setDiscountViewDetails] = useState<DiscountDetail>({});
    const [ocupancyDetails, setOcupancyDetails] = useState<OccupancyCounts | null>(null);
    const [initialOcupancy, setInitialOcupancy] = useState<OccupancyCounts | null>(null);
    const [adjustmentRequest, setAdjustmentRequest] = useState<AdjustmentPreviewRequest | null>(null);
    const [didPreviewFailed, setDidPreviewFailed] = useState<AdjustmentErrors | null>(null);
    const [didPaymentFailed, setDidPaymentFailed] = useState<boolean>(false);
    const [trackingExtras, setTrackingExtras] = useState<{ new_reservation_total?: number; discount_amount?: number }>({});
    const [previewLoading, setPreviewLoading] = useState<boolean>(false);

    // Feature Flag
    const {
        isEnabled: isTripAddNightEnable,
        config: tripAddNightConfig,
        clientReady,
    } = useFeatureConfig<AdjustmentConfig>(Splits.GUEST_PORTAL_ADD_NIGHT_RESERVATION_SERVICE, false);

    const {
        isEnabled: isPoolHeatEnable,
        config: poolHeatConfig,
        clientReady: poolHeatClientReady,
    } = useFeatureConfig<AdjustmentConfig>(Splits.GUEST_PORTAL_POOL_HEAT, true);

    const { optimizely: optimizelyInstance } = useContext(OptimizelyContext);

    let { adjustment } = params;
    const { guests, stay, reservation, home_info, unit } = trip;

    const { search } = window.location;
    const searchParams = new URLSearchParams(search);
    const isAddNightAdj = adjustment === 'add-night' || adjustment === 'addNight';
    const isExtendStayAdjustment = adjustment === 'extended_stay';
    const isLateCheckoutAdjustment = adjustment === 'late-checkout';
    const isPoolHeatAdjustment = adjustment === 'pool-heat';

    const initialLoading = isAddNightAdj || isLateCheckoutAdjustment || isPoolHeatAdjustment;

    const daysPriorFirstNight = 2;
    const excludedSources = [{ id: 13, name: 'booking.com' }];

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    });

    useEffect(() => {
        if (_.isNil(reservation) || _.isNil(stay) || _.isNil(unit) || _.isNil(adjustmentRequest) || trip.isLoading || previewLoading) {
            return;
        }

        if (isAddNightAdj && clientReady) {
            const isAddNightTripEnabled = isFeatureAvailableForChannel(reservation, clientReady, tripAddNightConfig, isTripAddNightEnable);

            if (!isAddNightTripEnabled) {
                if (ReservationUtils.isOutOrphanNightConditionWindow(stay, daysPriorFirstNight)) {
                    ddLogger.info(`Not eligible for adjustment ${adjustmentRequest.name}`, {
                        reservation_id: reservation.id,
                    });
                    setDidPreviewFailed(AdjustmentErrors.POLICY_VALIDATION_FAILED);
                    segmentService.trackAdjustmentIssue(adjustmentRequest, AdjustmentErrors.TIME_FRAME_EXPIRED);
                    return;
                }
            }

            const excludedSourcesIds = _.find(excludedSources, function (o) {
                return Number(o.id) === Number(reservation.reservation_source_id);
            });

            const adjustmentError = getErrorAddNighAdjustment(isAddNightTripEnabled, reservation, adjustment, excludedSourcesIds);

            if (!_.isNil(adjustmentError)) {
                segmentService.trackAdjustmentIssue(adjustmentRequest, adjustmentError);
                setDidPreviewFailed(adjustmentError);
                return;
            }

            (async () => {
                await getAdjustmentPreview(reservation.id, adjustmentRequest);
            })();
        }

        if (isLateCheckoutAdjustment) {
            if (!unitSupportsLateCheckout(unit!)) {
                history.push(AppRoutes.RESERVATION);
            }
            if (reservation.balance > 0) {
                ddLogger.info(`Reservation has a balance, not eligible for adjustment ${adjustmentRequest.name}`, {
                    reservation_id: reservation.id,
                    balance: reservation.balance,
                });
                setDidPreviewFailed(AdjustmentErrors.PENDING_BALANCE);
                return;
            }
            const lateCheckoutAvailable = stay.late_check_out_available;
            if (!lateCheckoutAvailable) {
                const checkout = stay.check_out_date;

                const unitTimezone = unit.timezone || 'America/Los_Angeles';
                const data = {
                    checkout,
                    utm_medium: searchParams.get('utm_medium') ?? 'web',
                    utm_campaign: searchParams.get('utm_campaign') ?? 'web',
                    current_date: moment().tz(unitTimezone).format(),
                };

                ddLogger.info(`Not eligible for adjustment ${adjustmentRequest.name}`, data);
                setDidPreviewFailed(AdjustmentErrors.POLICY_VALIDATION_FAILED);
                segmentService.trackAdjustmentIssue(adjustmentRequest, AdjustmentErrors.TIME_FRAME_EXPIRED, data);

                return;
            }
            (async () => {
                await getAdjustmentPreview(reservation.id, adjustmentRequest);
            })();
        }

        if (isPoolHeatAdjustment) {
            const isPoolHeatEnabled = isFeatureAvailableForChannel(reservation, poolHeatClientReady, poolHeatConfig, isPoolHeatEnable);
            if (!isPoolHeatEnabled) {
                ddLogger.info(`Not eligible for adjustment ${adjustmentRequest.name}`, {
                    reservation_id: reservation.id,
                });
                setDidPreviewFailed(AdjustmentErrors.ADJUSTMENT_NOT_IMPLEMENTED);
                segmentService.trackAdjustmentIssue(adjustmentRequest, AdjustmentErrors.RESERVATION_SOURCE_EXCLUDED);
                return;
            }

            const poolHeatAvailable = stay.pool_heat_available;

            if (!poolHeatAvailable) {
                ddLogger.info(`Not eligible for adjustment ${adjustmentRequest.name}`);
                setDidPreviewFailed(AdjustmentErrors.POLICY_VALIDATION_FAILED);
                segmentService.trackAdjustmentIssue(adjustmentRequest, AdjustmentErrors.TIME_FRAME_EXPIRED);
                return;
            }

            (async () => {
                await getAdjustmentPreview(reservation.id, adjustmentRequest);
            })();
        }
    }, [adjustmentRequest, user.currency]);

    useEffect(() => {
        switch (adjustment) {
            case 'add-night' || 'addNight': {
                const isAddNightTripEnabled = isFeatureAvailableForChannel(
                    reservation!,
                    clientReady,
                    tripAddNightConfig,
                    isTripAddNightEnable
                );

                adjustment = isAddNightTripEnabled ? 'tripAddNight' : 'addNight';

                if (!searchParams.has('date')) {
                    ddLogger.info('addNight adjustment is missing date, redirecting to home');
                    history.push(AppRoutes.RESERVATION);
                }
                // base on feature flag, change name of the adjustment
                const addNightFlow = isAddNightTripEnabled ? 'tripAddNight' : 'addNight';

                setAdjustmentRequest({
                    name: addNightFlow,
                    data: {
                        date: searchParams.get('date'),
                        vxuser: searchParams.get('vxuser') || undefined,
                        discount_coupon: searchParams.get('discount_coupon') || undefined,
                    },
                } as AdjustmentPreviewRequest);

                break;
            }
            case 'late-checkout':
                setAdjustmentRequest({ name: 'lateCheckout' } as AdjustmentPreviewRequest);
                break;
            case 'pool-heat':
                setAdjustmentRequest({ name: 'poolHeat' } as AdjustmentPreviewRequest);
                break;
            case 'extended_stay': {
                if (_.isNil(location) || _.isNil(location.state) || _.isNil(location.state.preview)) {
                    ddLogger.info('extendedStay adjustment have missing state, redirecting to home');
                    history.push(AppRoutes.RESERVATION);
                }

                const preview = location.state.preview as TripAdjustmentResponse;

                segmentService.trackAdjustmentSuccess(Step.PREVIEW, preview, {
                    reservation_checkin: stay?.check_in_date,
                    reservation_checkout: stay?.check_out_date,
                    reservation_balance: reservation?.balance,
                });

                setAdjustmentRequest({
                    name: 'extendedStay',
                    data: {
                        first_night: (preview?.adjustment as ExtendStayPreviewRequest).data.first_night,
                        last_night: (preview?.adjustment as ExtendStayPreviewRequest).data.last_night,
                    },
                } as AdjustmentPreviewRequest);

                setPreview(preview);

                break;
            }
            case 'modify_pets': {
                if (
                    _.isNil(location) ||
                    _.isNil(location.state) ||
                    _.isNil(location.state.preview) ||
                    _.isNil(location.state.occupancyCounts) ||
                    _.isNil(location.state.initialOccupancy)
                ) {
                    ddLogger.info('updatePetAdjustment adjustment have missing state, redirecting to home');
                    history.push(AppRoutes.RESERVATION);
                }

                const preview = location.state.preview as TripAdjustmentResponse;

                segmentService.trackAdjustmentSuccess(Step.PREVIEW, preview, {
                    pets: preview.pets,
                    reservation_balance: reservation?.balance,
                });

                setAdjustmentRequest({
                    name: 'updatePetAdjustment',
                    data: {
                        pets: preview.pets,
                    },
                } as AdjustmentPreviewRequest);

                setPreview(preview);
                setInitialOcupancy(location.state.initialOccupancy!);
                setOcupancyDetails(location.state.occupancyCounts!);

                break;
            }
            default:
                history.push(AppRoutes.RESERVATION);
        }
    }, [clientReady, isTripAddNightEnable, reservation]);

    const getAdjustmentPreview = async (reservationId: string, adjustment: AdjustmentPreviewRequest) => {
        try {
            ddLogger.info(`previewing adjustment ${adjustment.name} for ${reservationId}`);
            const tripService = new TripService();
            setPreviewLoading(true);
            let preview: TripAdjustmentResponse | null = null;
            let trackingExtras: any;
            let loggerData: any;

            const isAddNightTripEnabled = isFeatureAvailableForChannel(reservation!, clientReady, tripAddNightConfig, isTripAddNightEnable);

            if (isAddNightAdj && isAddNightTripEnabled) {
                const {
                    adjustmentPreview,
                    discountDetails,
                    trackingExtras: objTrack,
                } = await getTripAddNightPreview(tripService, isTripAddNightEnable, reservation!, adjustment);
                preview = adjustmentPreview;
                setDiscountViewDetails(discountDetails);
                trackingExtras = objTrack;
                loggerData = { delta: adjustmentPreview.delta };
            } else if (isAddNightAdj) {
                const {
                    discountedResponse,
                    discountDetails,
                    trackingExtras: objTrack,
                    regularResponse,
                } = await getPublicAddNightPreview(tripService, isTripAddNightEnable, reservation!, adjustment);
                preview = discountedResponse;
                setDiscountViewDetails(discountDetails);
                trackingExtras = objTrack;
                loggerData = {
                    delta: discountedResponse.delta,
                    original_total: regularResponse.attributes.delta.total,
                };
            }

            if (isLateCheckoutAdjustment) {
                const { adjustmentPreview, trackingExtras: objTrack } = await getLateCheckoutPreview(tripService, reservation!, adjustment);
                preview = adjustmentPreview;
                trackingExtras = objTrack;
                loggerData = { delta: preview.delta };
            }
            const isPoolHeatEnabled = isFeatureAvailableForChannel(reservation!, poolHeatClientReady, poolHeatConfig, isPoolHeatEnable);

            if (isPoolHeatAdjustment && isPoolHeatEnabled) {
                preview = await getPoolHeatPreview(tripService, reservation!, adjustment);
                loggerData = { delta: preview.delta };
            }

            setPreview(preview);
            setTrackingExtras(trackingExtras);
            segmentService.trackAdjustmentSuccess(Step.PREVIEW, preview!, trackingExtras);
            ddLogger.info(`adjustment ${adjustment.name} successfully previewed`, {
                ...loggerData,
            });
            setPreviewLoading(false);
        } catch (e) {
            const code = e.code;
            ddLogger.error(`ERROR previewing ${adjustment.name} adjustment for ${reservationId}`, {
                adjustment: adjustment.name,
                error: e.message,
                code,
            });
            setDidPreviewFailed(code);
            segmentService.trackAdjustmentIssue(adjustment, code);
        }
    };

    const getPaymentConfirmationProps = (
        preview: TripAdjustmentResponse,
        adjustmentName: AdjustmentPolicies
    ): PaymentConfirmationAdjustmentProps => {
        let paymentConfirmationProps: PaymentConfirmationAdjustmentProps;

        if (isAddNightAdj) {
            paymentConfirmationProps = {
                adjustment: adjustmentName,
                preview: preview,
                discount_details: {
                    adjustment_date: discountViewDetails.adjustment_date,
                    original_rent: discountViewDetails.original_rent,
                    discount_amount: discountViewDetails.discount_amount,
                    discount_rent: discountViewDetails?.discount_rent,
                },
            };

            segmentService.trackAdjustmentSuccess(Step.PAYMENT, preview, trackingExtras);
        } else if (isExtendStayAdjustment) {
            paymentConfirmationProps = {
                adjustment: adjustmentName,
                preview: preview,
            };

            segmentService.trackAdjustmentSuccess(Step.PAYMENT, preview, {
                reservation_checkin: stay?.check_in_date,
                reservation_checkout: stay?.check_out_date,
                reservation_balance: reservation?.balance,
            });
        } else {
            paymentConfirmationProps = {
                adjustment: adjustmentName,
                preview: preview,
                occupancy_details: {
                    initialOccupancy: initialOcupancy,
                    occupancyCounts: ocupancyDetails,
                },
            };

            segmentService.trackAdjustmentSuccess(Step.PAYMENT, preview, {
                pets: preview.pets,
                reservation_balance: reservation?.balance,
            });
        }

        return paymentConfirmationProps;
    };

    const persistAdjustment = async (reservationId: string, preview: TripAdjustmentResponse, values: PaymentFormValues, guests: Guests) => {
        try {
            const persistAdjustmentRequest = PaymentUtils.transformToPersistRequest(preview, guests.primary, preview.delta.total, values);

            ddLogger.info(`persisting adjustment for ${reservationId}`, { uuid: preview.uuid });

            const tripService = new TripService();
            const response = await tripService.persistAdjustment(reservationId, persistAdjustmentRequest);
            const { adjustment, delta, uuid, payment_status } = response.attributes as TripAdjustmentResponse;

            const paymentConfirmationProps = getPaymentConfirmationProps(preview, adjustment.name);

            ddLogger.info(`adjustment ${adjustment.name} successfully persisted`, { delta, uuid });

            if (adjustment.name === 'extendedStay' && optimizelyInstance) {
                optimizelyInstance.track('guest-portal-extend-stay-purchase-event');
            }
            if (payment_status === AdjustmentErrors.PAYMENT_ERROR) {
                setDidPaymentFailed(true);
                return;
            }

            history.push({
                pathname: AppRoutes.PAYMENT_SUCCESSFUL,
                state: paymentConfirmationProps,
            });
        } catch (e) {
            const code = e.code;
            ddLogger.error(`ERROR persisting ${preview.adjustment.name} adjustment for ${reservationId}`, {
                adjustment: preview.adjustment.name,
                error: e.message,
                code,
            });

            let extras = {};
            if (adjustmentRequest?.name === 'extendedStay') {
                extras = {
                    reservation_id: reservation?.id,
                    additional_nights: (preview.adjustment as ExtendStayPreviewRequest).data.additional_nights_count,
                };
            }
            segmentService.trackAdjustmentIssue(adjustmentRequest as AdjustmentPreviewRequest, code, extras);

            if (e.status === 504) {
                ddLogger.error(
                    `ERROR persisting it is taking longer than expected ${preview.adjustment.name} adjustment for ${reservationId}`,
                    {
                        adjustment: preview.adjustment.name,
                        error: e.message,
                        code,
                    }
                );
            }
            setDidPaymentFailed(true);
            return;
        }
    };

    if (!_.isNil(didPreviewFailed)) {
        return (
            <div>
                <AdjustmentNotAvailableModal reason={didPreviewFailed} adjustment={adjustmentRequest} />
            </div>
        );
    }

    if (initialLoading && (trip.isLoading || !clientReady || previewLoading || !poolHeatClientReady)) {
        return <Loading />;
    }

    if (_.isNil(preview)) {
        return null;
    }

    if (!guests || !stay || !reservation || !home_info || !unit) {
        return null;
    }

    const {
        delta,
        converted_delta,
        adjustment: { name },
    } = preview;

    return (
        <div id={'main'}>
            <div className={'row'}>
                <div className={'col-12 col-md-7'}>
                    {didPaymentFailed && <FailedPayment adjustment={name} />}
                    {!didPaymentFailed && <AdjustmentPanel preview={preview} adjustment={name} />}
                    <div className={'d-md-none'}>
                        <div className={'type-body-small reservation-details-mobile px-16 py-24'}>
                            <ReservationDetails
                                preview={preview}
                                discount_details={discountViewDetails}
                                occupancy_details={{
                                    initialOccupancy: initialOcupancy,
                                    occupancyCounts: ocupancyDetails,
                                }}
                            />
                        </div>
                    </div>
                    {!didPaymentFailed && (
                        <React.Fragment>
                            <div className="d-none d-md-block p-2">
                                <ReceiptTotalTitle
                                    summary={{
                                        total: delta.total,
                                        currency: delta.currency,
                                    }}
                                    convertedSummary={converted_delta}
                                    locale={intl.locale}
                                />
                            </div>
                            <PaymentForm
                                guests={guests}
                                currency={reservation.booked_currency_code as Currency}
                                onSubmit={(values) => {
                                    return persistAdjustment(reservation.id, preview, values, guests);
                                }}
                            />
                        </React.Fragment>
                    )}
                </div>

                <div className={'d-none d-md-block col-md-5'}>
                    <ReservationDetails
                        preview={preview}
                        discount_details={discountViewDetails}
                        occupancy_details={{
                            initialOccupancy: initialOcupancy,
                            occupancyCounts: ocupancyDetails,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
